import React, { useState } from 'react';
import './FeedbackPopup.css'; // Add styles for the popup
import posthog from 'posthog-js';
import { Button, Popover } from '@mui/material';

interface FeedBackPopupProps {
    isVisible: boolean;
    onClose: () => void;
    buildingId?: string;
}

const FeedBackPopup: React.FC<FeedBackPopupProps> = ({ isVisible, onClose, buildingId }) => {
    const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;

    posthog.init(POSTHOG_API_KEY, { 
        api_host: 'https://t.syte.ms/', 
        defaults: '2025-05-24', 
        autocapture: false, 
    })

    const [formData, setFormData] = useState({
        detectionAccuracy: '',
        panelPlacementQuality: '',
        overallToolUsefulness: '',
        email: '',
        surfaceDetectionIssues: [],
        technicalProblems: [],
        featureRequests: '',
        location: buildingId || '',
    });

    const [errors, setErrors] = useState({
        email: '',
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateEmail(formData.email)) {
            setErrors({ email: 'Please enter a valid email address.' });
            return;
        }

        setErrors({ email: '' });

        // Record event using PostHog API
        posthog.identify(formData.email);
        posthog.capture('pvpro_feedback_submitted', {
            detectionAccuracy: formData.detectionAccuracy,
            panelPlacementQuality: formData.panelPlacementQuality,
            overallToolUsefulness: formData.overallToolUsefulness,
            email: formData.email,
            surfaceDetectionIssues: formData.surfaceDetectionIssues,
            technicalProblems: formData.technicalProblems,
            featureRequests: formData.featureRequests,
            location: formData.location,
        });

        // Close the popup after submission
        onClose();
    };

    if (!isVisible) return null;

    return (
        <div className="feedback-popup-overlay">
            <Popover anchorOrigin={{ vertical: 'top', horizontal: 'left' }} open={isVisible} onClose={onClose} className="feedback-popup">
                <Button className="close-button" onClick={onClose} size='small'>
                    &times;
                </Button>
                <h2>Wir freuen uns über Ihr Feedback!</h2>
                <form onSubmit={handleSubmit}>
                {buildingId && (<label>
                        Störflächenerkennung:
                        <div className="radio-group">
                            {[1, 2, 3, 4, 5].map((value) => (
                                <label key={value} className="star-rating">
                                    <input
                                        type="radio"
                                        name="detectionAccuracy"
                                        value={value}
                                        checked={formData.detectionAccuracy === value.toString()}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {'★'.repeat(value)}
                                    {'☆'.repeat(5 - value)}
                                </label>
                            ))}
                        </div>
                    </label>)}
                    {buildingId && (
                        <label>
                            Modulauslegung:
                            <div className="radio-group">
                                {[1, 2, 3, 4, 5].map((value) => (
                                    <label key={value} className="star-rating">
                                        <input
                                            type="radio"
                                            name="panelPlacementQuality"
                                            value={value}
                                            checked={formData.panelPlacementQuality === value.toString()}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        {'★'.repeat(value)}
                                        {'☆'.repeat(5 - value)}
                                    </label>
                                ))}
                            </div>
                        </label>
                    )}
                    <label>
                        Nützlichkeit des Tools:
                        <div className="radio-group">
                            {[1, 2, 3, 4, 5].map((value) => (
                                <label key={value} className="star-rating">
                                    <input
                                        type="radio"
                                        name="overallToolUsefulness"
                                        value={value}
                                        checked={formData.overallToolUsefulness === value.toString()}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {'★'.repeat(value)}
                                    {'☆'.repeat(5 - value)}
                                </label>
                            ))}
                        </div>
                    </label>

                    {buildingId && (
                    <label>
                        Probleme bei der Störflächenerkennung:
                        <div className="checkbox-group">
                            {['Falsche Störfläche', 'Fehlende Störfläche', 'Ungenaue Abgrenzung'].map((issue) => (
                                <label key={issue}>
                                    <input
                                        type="checkbox"
                                        name="surfaceDetectionIssues"
                                        value={issue}
                                        onChange={(e) => {
                                            const { value, checked } = e.target;
                                            setFormData((prev) => ({
                                                ...prev,
                                                surfaceDetectionIssues: checked
                                                    ? [...(prev.surfaceDetectionIssues || []), value]
                                                    : (prev.surfaceDetectionIssues || []).filter((item) => item !== value),
                                            }));
                                        }}
                                    />
                                    {issue}
                                </label>
                            ))}
                        </div>
                    </label>)}
                    <label>
                        Technische Probleme:
                        <div className="checkbox-group">
                            {['Ladefehler', 'Berechnungsfehler', 'Anzeigeprobleme'].map((problem) => (
                                <label key={problem}>
                                    <input
                                        type="checkbox"
                                        name="technicalProblems"
                                        value={problem}
                                        onChange={(e) => {
                                            const { value, checked } = e.target;
                                            setFormData((prev) => ({
                                                ...prev,
                                                technicalProblems: checked
                                                    ? [...(prev.technicalProblems || []), value]
                                                    : (prev.technicalProblems || []).filter((item) => item !== value),
                                            }));
                                        }}
                                    />
                                    {problem}
                                </label>
                            ))}
                        </div>
                    </label>
                    <label>
                        Funktionswünsche/Kommentare:
                        <textarea
                            name="featureRequests"
                            value={formData.featureRequests || ''}
                            onChange={(e) => setFormData({ ...formData, featureRequests: e.target.value })}
                        />
                    </label>
                    <label>
                        Adresse-/Standort:
                        <input
                            type="text"
                            name="location"
                            value={buildingId || formData.location || ''}
                            readOnly={buildingId != undefined}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        />
                    </label>
                    <label>
                        Email-Adresse:
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.email && <span className="error">{errors.email}</span>}
                    </label>
                    <Button type="submit">Abschicken</Button>
                </form>
            </Popover>
        </div>
    );
};

export default FeedBackPopup;